import "./styles.scss";

import { useEffect, useRef, useState } from "react";
import api from "@helpers/api";
import CustomModal from "@components/CustomModal";
import { Input, Text, Button, Popper } from "@fragments";
import {
  CommunityAssetImageField,
  FileDropAssetField,
  AssetType,
  AssetCategory,
} from "@features/uploadAsset";
import FormBuilderModal from "@components/_modals/Forms/FormBuilderModal";
import {
  IconPlus,
  IconWorld,
  IconGeometry,
  IconChevronDown,
  IconFileDescription,
  IconSchool,
  IconPercentage,
  IconCreditCard,
  IconClipboardText,
  IconFileText,
} from "@tabler/icons-react";

import { DURATION_UNIT_OPTIONS } from "../../../enums/durationUnits";
import { adaptProgram } from "../../../helpers/apiAdapters";

const APPLICATION_OPTIONS = [
  { label: "Formulário Ginastee", value: "form" },
  { label: "Link externo", value: "external" },
  { label: "Sem inscrição", value: "none" },
];

// Name cap mirrors the backend (max:255); description is a product-defined
// cap (the backend has no validation rule for it).
const NAME_MAX = 255;
const DESCRIPTION_MAX = 1000;

const DURATION_SUGGESTIONS = [1, 4, 6, 8, 12];
const SPLIT_SUGGESTIONS = [5, 10, 15, 25, 50];

const INITIAL = {
  name: "",
  description: "",
  banner_url: "",
  duration_value: "",
  duration_unit: "weeks",
  split_enabled: false,
  split_duration_value: "",
  split_duration_unit: "months",
  split_percentage: "",
  plan_id: "",
  application_type: "form",
  application_form_id: "",
  application_url: "",
  contract_url: "",
  active: false,
};

/**
 * Program editor — create / edit (§6.2). Imperative open like ManageCouponModal:
 *   modalRef.current.open(program?, onSaved)
 * Banner upload is real (CommunityAssetImageField → uploaded URL). Plans and
 * forms come from their real endpoints; create/update hit the real program
 * endpoints with 422 → field mapping. Publishing is a status select in the
 * modal header (mirrors the community/general page); it stays local until save.
 */
export default function ProgramEditorModal({ modalRef, community }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});

  const [plans, setPlans] = useState([]);
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);

  const editingIdRef = useRef(null);
  const onSavedRef = useRef(null);
  const formBuilderRef = useRef({});
  const statusPopperRef = useRef();
  // Once the owner edits the commission window by hand, it stops mirroring the
  // mentoring duration.
  const splitDurationTouchedRef = useRef(false);

  const communityId = community?.id;

  modalRef.current = {
    open(program, onSavedCb) {
      editingIdRef.current = program?.id ?? null;
      onSavedRef.current = onSavedCb;
      splitDurationTouchedRef.current = !!program?.split_duration_value;
      setForm(program ? formFromProgram(program) : INITIAL);
      setErrors({});
      setOpen(true);
    },
  };

  const isEditing = !!editingIdRef.current;
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Mentoring duration drives the commission window while the latter is
  // untouched, so both stay in sync by default.
  function setMentoringDuration(value) {
    setForm((f) => {
      const next = { ...f, duration_value: value };
      if (!splitDurationTouchedRef.current) {
        next.split_duration_value = value;
        next.split_duration_unit = f.duration_unit;
      }
      return next;
    });
  }

  function setMentoringUnit(value) {
    setForm((f) => {
      const next = { ...f, duration_unit: value };
      if (!splitDurationTouchedRef.current) next.split_duration_unit = value;
      return next;
    });
  }

  function setSplitDuration(value) {
    splitDurationTouchedRef.current = true;
    set("split_duration_value", value);
  }

  function setSplitUnit(value) {
    splitDurationTouchedRef.current = true;
    set("split_duration_unit", value);
  }

  // Opening the commission card prefills its window with the mentoring
  // duration when still empty.
  function toggleSplit(on) {
    setForm((f) => {
      const next = { ...f, split_enabled: on };
      if (on && !f.split_duration_value && f.duration_value) {
        next.split_duration_value = f.duration_value;
        next.split_duration_unit = f.duration_unit;
      }
      return next;
    });
  }

  // Fetch the community's plans + forms when the modal opens (real endpoints).
  const loadForms = async () => {
    if (!communityId) return;
    setFormsLoading(true);
    try {
      const { data } = await api.get(`communities/${communityId}/forms`);
      setForms(Array.isArray(data) ? data : (data?.data ?? []));
    } catch (e) {
      console.error("Failed to load forms", e);
    } finally {
      setFormsLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !communityId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/community/${communityId}/plan`);
        if (alive) setPlans(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Failed to load plans", e);
      }
    })();
    loadForms();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, communityId]);

  async function handleSubmit() {
    setLoading(true);
    setErrors({});

    const payload = buildPayload(form);

    const res = isEditing
      ? await api.patch(
          `community/${communityId}/mentoring/programs/${editingIdRef.current}`,
          payload,
        )
      : await api.post(`community/${communityId}/mentoring/programs`, payload);

    setLoading(false);

    if (res?.response?.status >= 400) {
      setErrors(mapErrors(res.response.data?.errors));
      window.setSnackbar?.(
        res.response.data?.message || "Não foi possível salvar o programa",
      );
      return;
    }

    window.setSnackbar?.(isEditing ? "Programa atualizado" : "Programa criado");
    onSavedRef.current?.(adaptProgram(res.data));
    setOpen(false);
  }

  // ── Status select (header) — mirrors the community/general page ──────────
  function statusOptionLabel(title, description) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>{title}</span>
        <span style={{ fontSize: 12, opacity: 0.6, lineHeight: 1.3 }}>
          {description}
        </span>
      </div>
    );
  }

  function openStatusMenu(event) {
    const anchor = event.currentTarget;
    statusPopperRef.current?.open(
      [
        {
          id: "published",
          label: statusOptionLabel(
            "Publicado",
            "Visível para inscrição de mentorados",
          ),
          variant: form.active ? "info" : undefined,
          icon: <IconWorld size={20} />,
          onClick: async () => {
            if (!form.contract_url) {
              window.setSnackbar?.(
                "Adicione o contrato (PDF) para poder publicar",
              );
              return;
            }
            set("active", true);
          },
        },
        {
          id: "draft",
          label: statusOptionLabel("Rascunho", "Visível apenas para você"),
          variant: !form.active ? "info" : undefined,
          icon: <IconGeometry size={20} />,
          onClick: async () => set("active", false),
        },
      ],
      "bottom-end",
      anchor,
    );
  }

  const planSource = plans.length ? plans : (community?.plans ?? []);
  const planOptions = [
    { label: "Sem plano vinculado", value: "" },
    ...planSource.map((plan) => ({
      label: `${plan.label} — ${formatBrl(plan.price)}`,
      value: plan.id,
    })),
  ];

  const formOptions = forms.map((f) => ({
    label: f.title || `Formulário ${f.id}`,
    value: f.id,
  }));

  function openFormBuilder() {
    formBuilderRef.current.open?.(community, () => {
      // Form saved — refetch so the new one is selectable.
      loadForms();
      window.setSnackbar?.("Formulário criado — selecione-o na lista");
    });
  }

  return (
    <>
      <CustomModal
        maxWidth={600}
        open={open}
        handleClose={() => setOpen(false)}
        title={isEditing ? "Editar programa" : "Criar programa de mentoria"}
        topActions={
          <Button
            size="small"
            color={form.active ? "success" : "overlay"}
            startIcon={
              form.active ? <IconWorld size={15} /> : <IconGeometry size={15} />
            }
            endIcon={<IconChevronDown size={14} />}
            text={form.active ? "Publicado" : "Rascunho"}
            onMouseEnter={openStatusMenu}
            onClick={openStatusMenu}
            preventLoading
            className="mr-2"
          />
        }
        bottomButtonSumbitText={
          isEditing ? "Salvar alterações" : "Criar programa"
        }
        handleSubmit={handleSubmit}
        loading={loading}
        disabled={uploading}
        bodyComponent={
          open ? (
            <div className="program-editor">
              {/* ── Dados básicos ─────────────────────────────────── */}
              <Section
                title="Dados básicos"
                icon={<IconFileDescription size={16} />}
              >
                <Input.Text
                  label="Nome do programa"
                  value={form.name}
                  onInput={(v) => set("name", v)}
                  size={NAME_MAX}
                  error={!!errors.name}
                  helper={errors.name}
                  autoFocus={!isEditing}
                  placeholder="Ex.: Mentoria Aceleração 360"
                />
                <Input.Text
                  label="Descrição"
                  value={form.description}
                  onInput={(v) => set("description", v)}
                  size={DESCRIPTION_MAX}
                  multiline
                  placeholder="Explique o que o criador recebe (suporta Markdown)"
                />
                <div
                  style={{ maxWidth: "210px", margin: "0 auto", width: "100%" }}
                >
                  <Text.span contrast={6} className="pe-field-label">
                    Banner (4:3)
                  </Text.span>
                  <CommunityAssetImageField
                    communityId={communityId}
                    category={AssetCategory.COMMUNITY_BANNER}
                    aspectRatio={4 / 3}
                    fullWidth
                    imageMaxWidth={1200}
                    title="Banner do programa"
                    value={form.banner_url}
                    onChange={(url) => set("banner_url", url)}
                    onUploadingChange={setUploading}
                  />
                </div>
              </Section>

              {/* ── Mentoria ──────────────────────────────────────── */}
              <Section title="Mentoria" icon={<IconSchool size={16} />}>
                <div className="pe-row">
                  <Input.Integer
                    label="Duração da mentoria"
                    value={form.duration_value}
                    onInput={setMentoringDuration}
                    max={3}
                    placeholder="Indefinida"
                  />
                  <Input.Select
                    label="Unidade"
                    value={form.duration_unit}
                    onInput={setMentoringUnit}
                    options={DURATION_UNIT_OPTIONS}
                  />
                </div>
                <Suggestions
                  options={DURATION_SUGGESTIONS}
                  value={form.duration_value}
                  onPick={(v) => setMentoringDuration(String(v))}
                />
              </Section>

              {/* ── Coprodução (switch card) ──────────────────────── */}
              <div className="pe-split-card">
                <div className="pe-split-head">
                  <Text.h5
                    contrast={8}
                    weight={500}
                    left={<IconPercentage size={16} />}
                  >
                    Receber coprodução pelas vendas do mentorado
                  </Text.h5>
                  <Input.Switch
                    size="small"
                    value={form.split_enabled}
                    onInput={toggleSplit}
                  />
                </div>

                {form.split_enabled && (
                  <div className="pe-split-body">
                    <Input.Integer
                      label="Repasse de receita"
                      value={form.split_percentage}
                      onInput={(v) => set("split_percentage", v)}
                      max={3}
                      className="pe-split-percentile-input"
                      end="%"
                      error={!!errors.split_percentage}
                      helper={errors.split_percentage}
                    />
                    <Suggestions
                      options={SPLIT_SUGGESTIONS}
                      value={form.split_percentage}
                      onPick={(v) => set("split_percentage", String(v))}
                      format={(v) => `${v}%`}
                    />
                    <div className="pe-row">
                      <Input.Integer
                        label="Duração do repasse"
                        value={form.split_duration_value}
                        onInput={setSplitDuration}
                        max={3}
                        helper="Por padrão, a mesma duração da mentoria"
                      />
                      <Input.Select
                        label="Unidade"
                        value={form.split_duration_unit}
                        onInput={setSplitUnit}
                        options={DURATION_UNIT_OPTIONS}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ── Pagamento da mentoria ─────────────────────────── */}
              <Section
                title="Pagamento da mentoria"
                icon={<IconCreditCard size={16} />}
                subtitle="Informe um plano em que os mentorados deverão adquirir para entrar no programa de mentoria"
              >
                <Input.Select
                  label="Plano vinculado (opcional)"
                  value={form.plan_id}
                  onInput={(v) => set("plan_id", v)}
                  options={planOptions}
                  placeholder="Sem plano vinculado"
                />
              </Section>

              {/* ── Inscrição ─────────────────────────────────────── */}
              <Section
                title="Inscrição"
                icon={<IconClipboardText size={16} />}
                subtitle="Como os mentorados irão se inscrever na mentoria?"
              >
                <Input.Choices
                  value={form.application_type}
                  onInput={(v) => set("application_type", v)}
                  options={APPLICATION_OPTIONS}
                />

                {form.application_type === "form" && (
                  <div className="pe-form-picker">
                    <Input.Select
                      label="Formulário"
                      value={form.application_form_id}
                      onInput={(v) => set("application_form_id", v)}
                      options={formOptions}
                      placeholder={
                        formsLoading
                          ? "Carregando formulários..."
                          : formOptions.length
                            ? "Selecione um formulário"
                            : "Nenhum formulário ainda"
                      }
                    />
                    <Button
                      text="Criar novo formulário"
                      color="overlay"
                      size="small"
                      startIcon={<IconPlus size={16} />}
                      onClick={openFormBuilder}
                      preventLoading
                    />
                  </div>
                )}

                {form.application_type === "external" && (
                  <Input.Text
                    label="Link externo de inscrição"
                    value={form.application_url}
                    onInput={(v) => set("application_url", v)}
                    placeholder="https://..."
                  />
                )}
              </Section>

              {/* ── Contrato ──────────────────────────────────────── */}
              <Section title="Contrato" subtitle="Adicione um contrato para formalizar a entrada na mentoria." icon={<IconFileText size={16} />}>
                <div style={{marginTop: '-20px'}}/>
                <FileDropAssetField
                  communityId={communityId}
                  category={AssetCategory.MENTORING_CONTRACT}
                  assetType={AssetType.PDF}
                  maxBytes={10 * 1024 * 1024}
                  path={form.contract_url}
                  onPathChange={(url) => set("contract_url", url || "")}
                  onUploadingChange={setUploading}
                />
                <Text.span contrast={5}>
                  {form.contract_url
                    ? "Programas publicados ficam visíveis para inscrição."
                    : "Adicione o contrato (PDF) para poder publicar."}
                </Text.span>
                {errors.active && (
                  <Text.span className="pe-error">{errors.active}</Text.span>
                )}
              </Section>
            </div>
          ) : null
        }
      />

      <FormBuilderModal modalRef={formBuilderRef} communityId={communityId} />
      <Popper controlRef={statusPopperRef} config={{ activateOnHover: true }} />
    </>
  );
}

function Section({ title, icon, subtitle, children }) {
  return (
    <div className="pe-section">
      <div className="pe-section-head">
        <Text.h5
          contrast={8}
          weight={500}
          left={icon}
          className="pe-section-title"
        >
          {title}
        </Text.h5>
        {subtitle && (
          <Text.span contrast={5} className="pe-section-subtitle">
            {subtitle}
          </Text.span>
        )}
      </div>
      <div className="pe-section-body">{children}</div>
    </div>
  );
}

// Quick-pick pills rendered under an input (micro buttons). The active value
// is highlighted; clicking always applies the suggestion.
function Suggestions({ options, value, onPick, format = (v) => v }) {
  return (
    <div className="pe-suggestions">
      {options.map((option) => (
        <Button
          key={option}
          size="micro"
          variant="rounded"
          color={String(value) === String(option) ? "primary" : "overlay"}
          text={format(option)}
          onClick={() => onPick(option)}
          preventLoading
        />
      ))}
    </div>
  );
}

// ── transforms ───────────────────────────────────────────────────────────
function formFromProgram(program) {
  return {
    name: program.name ?? "",
    description: program.description ?? "",
    banner_url: program.banner?.url ?? "",
    duration_value: program.duration_value ?? "",
    duration_unit: program.duration_unit ?? "weeks",
    split_enabled:
      Number(program.split_percentage) > 0 || !!program.split_duration_value,
    split_duration_value: program.split_duration_value ?? "",
    split_duration_unit: program.split_duration_unit ?? "months",
    split_percentage: program.split_percentage ?? "",
    plan_id: program.plan_id ?? "",
    application_type: program.application?.type ?? "form",
    application_form_id: program.application?.form_id ?? "",
    application_url: program.application?.url ?? "",
    contract_url: program.contract_url ?? "",
    active: !!program.active,
  };
}

function buildPayload(form) {
  // The backend only knows type form|external — "Sem inscrição" is a null
  // application (CreateMentoringProgramRequest).
  let application = null;
  if (form.application_type === "external") {
    application = {
      type: "external",
      form_id: null,
      url: form.application_url || null,
    };
  } else if (form.application_type === "form") {
    application = {
      type: "form",
      form_id: form.application_form_id || null,
      url: null,
    };
  }

  const splitOn = form.split_enabled;

  return {
    name: form.name,
    description: form.description,
    banner: { url: form.banner_url || null, type: "image" },
    duration_value: form.duration_value || null,
    duration_unit: form.duration_value ? form.duration_unit : null,
    split_duration_value: (splitOn && form.split_duration_value) || null,
    split_duration_unit:
      splitOn && form.split_duration_value ? form.split_duration_unit : null,
    split_percentage:
      splitOn && form.split_percentage !== ""
        ? Number(form.split_percentage)
        : 0,
    plan_id: form.plan_id || null,
    application,
    contract_url: form.contract_url || null,
    active: form.active,
  };
}

function mapErrors(fieldErrors = {}) {
  return Object.entries(fieldErrors).reduce((acc, [key, messages]) => {
    acc[key] = Array.isArray(messages) ? messages[0] : String(messages);
    return acc;
  }, {});
}

function formatBrl(cents) {
  return `R$ ${((cents || 0) / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
